import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import {
  Users, Search, Loader2, CheckCircle, AlertTriangle, HelpCircle,
  Mail, Phone, Building2, ArrowLeft, Package, Ticket, ExternalLink, RefreshCw,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

const STATUS_COLORS = {
  MAPPED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CONTACT_UNRESOLVED: 'bg-amber-100 text-amber-700 border-amber-200',
  CUSTOMER_MAPPING_REQUIRED: 'bg-red-100 text-red-700 border-red-200',
  UNRESOLVED: 'bg-slate-100 text-slate-600 border-slate-200',
  CONFLICT: 'bg-red-100 text-red-700 border-red-200',
};

export default function NinjaEndUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const params = { skip: page * 50, limit: 50 };
      if (search) params.search = search;
      if (statusFilter) params.mapping_status = statusFilter;
      const res = await api.get('/ninja-end-users/', { params });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch { /* may not have data yet */ }
    setLoading(false);
  }, [page, search, statusFilter]);

  const loadHealth = useCallback(async () => {
    try {
      const res = await api.get('/ninja-end-users/health');
      setHealth(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadUsers(); loadHealth(); }, [loadUsers, loadHealth]);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.get('/ninja-end-users/preview');
      setPreviewResult(res.data);
    } catch (e) {
      toast.error('Preview failed: ' + (e.response?.data?.detail || e.message));
    }
    setPreviewing(false);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.post('/ninja-end-users/import');
      toast.success(`Import complete: ${res.data.mapped || 0} mapped, ${res.data.created || 0} created, ${res.data.updated || 0} updated`);
      setPreviewResult(null);
      loadUsers();
      loadHealth();
    } catch (e) {
      toast.error('Import failed: ' + (e.response?.data?.detail || e.message));
    }
    setImporting(false);
  };

  return (
    <div className="space-y-6 p-6" data-testid="ninja-end-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ninjarmm')} data-testid="back-to-ninja">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              NinjaOne End Users
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage NinjaOne end user contacts mapped to CipherOne customers</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handlePreview} disabled={previewing} data-testid="preview-import-btn">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Search className="h-4 w-4 mr-1.5" />}
            Preview Import
          </Button>
          <Button size="sm" onClick={handleImport} disabled={importing} data-testid="run-import-btn">
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Import Now
          </Button>
        </div>
      </div>

      {/* Health Summary */}
      {health && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="eu-health-stats">
          <Card><CardContent className="p-3"><p className="text-[11px] font-medium text-gray-500 uppercase">Total</p><p className="text-xl font-bold">{health.total}</p></CardContent></Card>
          <Card className="border-emerald-200"><CardContent className="p-3"><p className="text-[11px] font-medium text-emerald-600 uppercase">Mapped</p><p className="text-xl font-bold text-emerald-600">{health.mapped}</p></CardContent></Card>
          <Card className="border-amber-200"><CardContent className="p-3"><p className="text-[11px] font-medium text-amber-600 uppercase">Unresolved</p><p className="text-xl font-bold text-amber-600">{health.unresolved}</p></CardContent></Card>
          <Card className="border-red-200"><CardContent className="p-3"><p className="text-[11px] font-medium text-red-600 uppercase">Conflicts</p><p className="text-xl font-bold text-red-600">{health.conflicts}</p></CardContent></Card>
          <Card><CardContent className="p-3"><p className="text-[11px] font-medium text-gray-500 uppercase">Last Sync</p><p className="text-sm font-medium text-gray-700 mt-1">{health.last_sync ? formatInCST(health.last_sync, 'h:mm a') : 'Never'}</p></CardContent></Card>
        </div>
      )}

      {/* Preview Result */}
      {previewResult && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="import-preview">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Import Preview</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p>Found: <strong>{previewResult.total_found}</strong> end users</p>
            <p>Would map existing: <strong>{previewResult.would_map || 0}</strong></p>
            <p>Would create: <strong>{previewResult.would_create || 0}</strong></p>
            <p>Would update: <strong>{previewResult.would_update || 0}</strong></p>
            <p className="text-amber-700">Customer mapping required: <strong>{previewResult.customer_mapping_required || 0}</strong></p>
            {previewResult.errors?.length > 0 && (
              <p className="text-red-600">Errors: {previewResult.errors.length}</p>
            )}
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={handleImport} disabled={importing}>
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Confirm Import
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPreviewResult(null)}>Dismiss</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1 max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search by name or email..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              className="pl-9" data-testid="eu-search" />
          </div>
        </div>
        <div className="flex gap-1.5" data-testid="eu-status-filters">
          {[
            { label: 'All', value: '' },
            { label: 'Mapped', value: 'MAPPED' },
            { label: 'Unresolved', value: 'UNRESOLVED' },
            { label: 'No Customer', value: 'CUSTOMER_MAPPING_REQUIRED' },
          ].map(f => (
            <button key={f.value} onClick={() => { setStatusFilter(f.value); setPage(0); }}
              className={`h-7 px-2.5 text-[11px] rounded-full border transition-colors ${
                statusFilter === f.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`} data-testid={`eu-filter-${f.value || 'all'}`}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="font-medium">No end users found</p>
              <p className="text-sm mt-1">Import NinjaOne end users to see them here</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Sync</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/integrations/ninjaone/end-users/${u.id}`)}
                    data-testid={`eu-row-${u.id}`}>
                    <TableCell className="font-medium">{u.display_name}</TableCell>
                    <TableCell className="text-sm text-gray-600">{u.email || '—'}</TableCell>
                    <TableCell className="text-sm">
                      {u.cipherone_customer_id ? (
                        <button onClick={e => { e.stopPropagation(); navigate(`/customers/${u.cipherone_customer_id}`); }}
                          className="text-blue-600 hover:underline text-left">
                          {u.cipherone_customer_id}
                        </button>
                      ) : <span className="text-gray-400">Unmapped</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS[u.mapping_status] || STATUS_COLORS.UNRESOLVED}`}>
                        {u.mapping_status === 'MAPPED' ? <CheckCircle className="h-3 w-3" /> :
                         u.mapping_status === 'CUSTOMER_MAPPING_REQUIRED' ? <AlertTriangle className="h-3 w-3" /> :
                         <HelpCircle className="h-3 w-3" />}
                        {u.mapping_status?.replace(/_/g, ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">{u.last_synced_at ? formatInCST(u.last_synced_at, 'MMM d, h:mm a') : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-gray-500 self-center">Page {page + 1} of {Math.ceil(total / 50)}</span>
          <Button size="sm" variant="outline" disabled={(page + 1) * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
